import type { TranslationSchema } from './ru';

export const en: TranslationSchema = {
  common: {
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    currency: 'sum',
    loading: 'Loading…',
    retry: 'Retry',
    save: 'Save changes',
    cancel: 'Cancel',
    confirm: 'Confirm',
    confirmDefault: 'Are you sure?',
    close: 'Close',
    delete: 'Delete',
    search: 'Search',
    all: 'All',
    export: 'Export',
    somethingWrong: 'Something went wrong',
    notFound: 'Page not found',
    noAccess: 'You do not have access to this section',
    back: 'Back',
    prev: 'Previous page',
    next: 'Next page',
    comingSoon: 'Section in progress',
    comingSoonSubtitle: 'This section will appear here soon',
  },
  units: {
    piece: 'pcs',
    kilogram: 'kg',
    meter: 'm',
    bag: 'bag',
    ton: 't',
    sheet: 'sheet',
    bucket: 'bkt',
    roll: 'roll',
    box: 'box',
    pack: 'pack',
    liter: 'l',
  },
  brand: {
    name: 'Buildix',
    footer: '© 2026 Buildix · Strotech',
  },
  sync: {
    unpaired: 'The shop computer is not paired with the cloud — data here is not updating.',
    unpairedShop: 'This computer is not linked to the cloud — sales work, but the owner cannot see them remotely.',
    staleShop: 'Nothing has reached the cloud for {{age}} — the owner sees stale figures remotely.',
    pushBroken: 'Data is not reaching the cloud. Sales keep working, but the owner sees stale figures remotely — contact support.',
    pushBrokenCloud: 'The shop is online, but no data is arriving — the figures here are stale. Contact support.',
    stale: 'Data last updated {{age}} ago — the shop is offline.',
    ageUnknown: 'a while',
    ageMinutes: '{{count}} min',
    ageHours: '{{count}} h',
    ageDays: '{{count}} d',
  },
  nav: {
    sections: {
      operations: 'Operations',
      clients: 'Clients',
      supply: 'Supply',
      management: 'Management',
    },
    dashboard: 'Dashboard',
    sales: 'Sales',
    cash: 'Cash',
    warehouse: 'Warehouse',
    products: 'Products & prices',
    categories: 'Categories',
    returns: 'Returns',
    debts: 'Debts',
    customers: 'Customers',
    purchases: 'Purchases',
    suppliers: 'Suppliers',
    shifts: 'Shifts',
    reports: 'Reports',
    employees: 'Employees',
    audit: 'Audit',
    notifications: 'Notifications',
    settings: 'Settings',
    account: 'My account',
    logout: 'Log out',
    menu: 'Menu',
    devices: 'Devices',
  },
  devices: {
    title: 'Devices',
    subtitle: 'Check the printer and scanner connection',
    printer: {
      connected: 'Connected printers',
      detect: 'Find the printer',
      usbFound: 'connected',
      usbHint: 'The browser cannot see printers on its own. Press "Find the printer" and pick your USB printer from the list that opens.',
      usbUnsupported: 'This browser cannot show USB devices (Chrome or Edge is needed). The test label still works.',
      usbNone: 'No connected USB printer was found or selected.',
      lastCheck: 'Last check: {{date}} {{time}}, {{size}} mm —',
      lastOk: 'worked',
      lastFailed: 'did not work',
      goToLabels: 'Print product labels',
      title: 'Label printer',
      subtitle: 'A browser cannot list the operating system printers, so the most reliable check is printing a real test label.',
      size: 'Label size',
      test: 'Print a test label',
      askResult: 'Did the label come out correctly?',
      yes: 'Yes',
      no: 'No',
      ok: 'The printer is connected and working.',
      popupBlocked: 'The browser blocked the new window, so the print dialog never opened. Allow pop-ups for buildix.uz or download the file.',
      download: 'Download the PDF',
      troubleTitle: 'No label came out — check the following:',
      trouble1: 'The printer is powered on and the USB/network cable is connected',
      trouble2: 'The print dialog has the label printer selected (not A4)',
      trouble3: 'The paper size in the printer settings matches the label size',
      trouble4: 'The label roll is not empty and calibration has been run',
    },
    scanner: {
      title: 'Barcode scanner (handheld or fixed)',
      subtitle: 'A scanner connects as a keyboard, so it cannot be found in a device list — but its typing speed gives it away.',
      placeholder: 'Scan any barcode into this field…',
      detected: 'Scanner detected — the speed is far above human typing.',
      typed: 'This was typed by hand. If a scanner is connected, scan the code again.',
      tooShort: 'Too short — at least 6 characters are needed to judge.',
      noEnter: 'The scanner does not send Enter at the end. Turn that on in its settings, otherwise the code will not add itself to the receipt.',
      code: 'Code',
      chars: 'Characters',
      speed: 'Average gap',
      ms: 'ms',
      checkInBase: 'Look up in the database',
      found: 'Found: {{name}}',
      notFound: 'This code is not in the database',
      hint: 'The check saves nothing. If the code is not found, the scanner works — the code simply is not attached to a product.',
    },
  },
  notifications: {
    subtitle: 'Unread: {{count}}',
    empty: 'All clear — no alerts',
    liveHint: 'Stock and debt alerts refresh automatically. Shift and delivery events arrive the moment they happen.',
    markAll: 'Mark all as read',
    viewAll: 'View all',
    today: 'Today',
    yesterday: 'Yesterday',
    cat: {
      warehouse: 'Warehouse',
      debt: 'Debts',
      shift: 'Shifts',
      supply: 'Supply',
    },
    withdrawRequest: 'Cash withdrawal request',
    debtOverdue: 'Debt overdue',
    debtDueToday: 'Debt due today',
    lowStock: 'Low stock',
    outOfStock: 'Out of stock',
  },
  seller: {
    nav: {
      pos: 'Register',
      sales: 'My Sales',
      products: 'Products',
      clients: 'Clients',
      debts: 'Debts',
      supplies: 'Supplies',
      shifts: 'Shifts',
      shiftOpen: 'Shift open',
      shiftClosed: 'Shift closed',
      openShift: 'Open shift',
      hoursShort: 'h',
      minsShort: 'm',
      account: 'Account',
      notifications: 'Notifications',
    },
    products: {
      title: 'Products',
      subtitle: 'Store catalogue · cost prices are visible to the owner',
      cols: { location: 'LOCATION' },
      card: {
        price: 'Sale price',
        stock: 'In stock',
        minStock: 'Min. stock',
        sku: 'SKU',
        barcode: 'Barcode',
        location: 'Warehouse location',
        supplier: 'Supplier',
        lastIn: 'Last receipt',
        soldMonth: 'Sold this month',
        sell: 'Sell — open the till',
        costHidden: 'Cost price and markup are visible only to the owner',
      },
    },
    debts: {
      acceptedToday: 'Accepted today',
      acceptedCols: { time: 'TIME', customer: 'CLIENT', method: 'METHOD', remaining: 'DEBT LEFT', amount: 'AMOUNT' },
      title: 'Customer debts',
      subtitle: 'Debt collection · payment goes into the current shift till',
    },
    clients: {
      title: 'Clients',
      subtitle: 'Store client base · {{count}} records',
      add: 'New client',
      addHint: 'Name and phone are enough — the rest can be filled in later.',
      searchPlaceholder: 'Search by name or phone…',
      empty: 'No clients found',
      regular: 'regular',
      filters: { all: 'All', withDebt: 'With debt', regular: 'Regulars' },
      purchasesCount: '{{count}} purchases',
      cols: { client: 'CLIENT', phone: 'PHONE', monthPurchases: 'PURCHASES THIS MONTH', lastPurchase: 'LAST', debt: 'DEBT' },
      form: { name: 'Name / company', phone: 'Phone', type: 'Client type' },
    },
    supplies: {
      title: 'Supplies',
      subtitle: 'Created by the administrator · you can view and receive',
      readOnly: 'View only — editing and receiving are handled by the administrator',
      received: 'received',
      allTitle: 'All supplies',
      accept: 'Start receiving',
      acceptOnArrival: 'Receiving opens on arrival',
      eta: 'arrival: {{date}}',
      positionsAmounts: '{{count}} positions · amounts visible to the owner',
      filters: { all: 'All', awaiting: 'Awaited', accepted: 'Received' },
      cols: { positions: 'POSITIONS' },
      state: { accepted: 'received', awaiting: 'awaiting receipt', inTransit: 'in transit', delayed: 'delayed' },
    },
    notifications: {
      subtitle: 'Active alerts: {{count}}',
      unread: 'Unread',
      empty: 'All clear — no alerts',
      liveHint: 'This list reflects the current state: an entry disappears once the issue is resolved.',
      debtOverdue: 'Debt overdue',
      debtDueToday: 'Debt due today',
      lowStock: 'Low stock',
      outOfStock: 'Out of stock',
    },
    pos: {
      allProducts: 'All products',
      outOfStock: 'out',
      priceOnRequest: 'Ask for price',
      receipt: 'Receipt',
      clear: 'Clear',
      discard: 'Delete receipt',
      discardConfirm: 'Delete this unfinished receipt? Items return to stock.',
      held: 'Parked receipts',
      park: 'Park',
      leaveTitle: 'Sale not finished',
      leaveMessage:
        'Park this receipt? It stays in the “Parked” list and you can pick it up again any time.',
      stay: 'Stay at the register',
      positions: 'Items',
      checkoutTitle: 'Payment',
      toPay: 'To pay',
      received: 'Received from customer',
      noChange: 'Exact',
      change: 'Change',
      short: 'Short by',
      confirm: 'Confirm sale',
      cardHint: 'Take the payment on the terminal and confirm once the transaction succeeds.',
      debtNeedsCustomer: 'Pick a customer from the base to sell on credit',
      paidNow: 'Paid now',
      debtRest: 'On credit (remaining)',
      doneTitle: 'Sale completed',
      print: 'Print receipt',
      newSale: 'New sale',
      thanks: 'Thank you for your purchase!',
      shift: 'Shift',
      qty: 'Quantity',
      qtyHint: 'Type the quantity — decimals allowed (12; 3.5; 0.25)',
      editPrice: 'Change price',
      openShift: 'Open shift',
      loadMore: 'Show more',
      shownOf: '{{shown}} of {{total}} products',
      refineSearch: 'Too many results — narrow the search',
      backToSearch: 'Back to search',
      external: {
        button: 'Not in catalogue',
        title: 'Off-catalogue item',
        subtitle: 'Does not move stock — added to this receipt only',
        name: 'Item / service name',
        price: 'Sale price',
        cost: 'Cost price (optional)',
        costHint: 'Left empty, this line reports as pure profit in the margin report.',
        costTooHigh: 'Cost price must be lower than the sale price.',
        add: 'Add to receipt',
      },
      askPrice: {
        title: 'Enter the price',
        price: 'Sale price',
        add: 'Add to receipt',
        hint: 'This product’s price is hidden from sellers — ask the owner for it and type it in.',
      },
    },
    shifts: {
      range: { week: 'Week', month: 'Month', all: 'All time' },
      total: 'Period total',
    },
    sales: {
      returnsStat: 'Returns',
      returnsSum: '· {{value}} sum',
      returnOf: 'return for receipt №{{number}}',
      returnHint: 'Returns are processed via the receipt card · past-shift history is in «My shifts»',
    },
  },
  auth: {
    title: 'Sign in',
    subtitle: 'Enter the credentials issued by the store owner or administrator.',
    usernameLabel: 'Login',
    usernamePlaceholder: 'Enter your login',
    passwordLabel: 'Password',
    passwordPlaceholder: '••••••••',
    forgotPassword: 'Forgot password?',
    submit: 'Sign in',
    submitting: 'Signing in…',
    contactAdmin: 'CONTACT ADMINISTRATOR',
    errors: {
      invalidCredentials: 'Invalid login or password',
      generic: 'Sign-in failed. Please try again.',
      consoleNotConfigured: 'The console is not configured — contact the server administrator.',
      marketMissing: 'This copy has no store data. Contact support.',
      tooManyAttempts: 'Too many attempts. Please try again later.',
    },
  },
  subscription: {
    expiredTitle: 'Subscription expired',
    expiredDesc: 'The store subscription has expired. Contact the administrator to renew.',
    blockedTitle: 'Store blocked',
    blockedDesc: 'Access to the store is temporarily suspended. Contact the administrator.',
  },
  categories: {
    title: 'Categories',
    subtitle: 'Product lines and the sales behind them',
    add: 'Add',
    edit: 'Edit',
    newPlaceholder: 'New category name…',
    empty: 'No categories yet. Add the first one above.',
    uncategorized: 'Uncategorized',
    deleteTitle: 'Delete category',
    deleteHint: '«{{name}}» will be deleted. Its {{count}} products are NOT removed — they simply lose the category.',
    profitNote: 'Profit for the period: {{value}}',
    stats: { count: 'Categories', products: 'Products', sales: 'Sales' },
    cols: { name: 'CATEGORY', products: 'PRODUCTS', sales: 'SALES', share: 'SHARE' },
    addPlaceholder: 'Category name…',
    deleteConfirm: 'Delete category “{{name}}”?',
  },
  warehouse: {
    title: 'Warehouse',
    subtitle: '{{count}} items · stock value {{value}} sum',
    subtitleShort: '{{count}} items · low: {{low}} · out: {{out}}',
    addProduct: 'Add product',
    newPurchase: 'New purchase',
    stocktake: 'Stocktake',
    searchPlaceholder: 'Search by name, SKU or barcode…',
    onlyLow: 'Low stock only',
    allCategories: 'All',
    showing: 'Showing {{shown}} of {{total}} items',
    empty: 'No products found',
    movementHint: 'Min. stock is edited right in the table. Click a row for stock movement and correction.',
    filter: { all: 'All', ok: 'In stock', low: 'Low', out: 'Out' },
    movement: {
      title: 'Stock movement',
      empty: 'No movements yet',
      correctionTitle: 'Correction (stocktake)',
      correctionHint: 'The variance is recorded as a correction movement and logged',
      actualQty: 'Actual stock',
      apply: 'Apply',
      type: {
        initialStock: 'Initial stock',
        purchase: 'Purchase',
        sale: 'Sale',
        saleReversal: 'Sale reversal',
        correction: 'Correction',
      },
    },
    stats: {
      positions: 'Total items',
      value: 'Stock value',
      low: 'Low stock',
      out: 'Out of stock',
    },
    cols: {
      product: 'PRODUCT',
      sku: 'SKU',
      category: 'CATEGORY',
      stock: 'STOCK',
      minStock: 'MIN. STOCK',
      cost: 'COST PRICE',
      price: 'SALE PRICE',
      status: 'STATUS',
      lastReceipt: 'LAST RECEIPT',
    },
    status: {
      inStock: 'In stock',
      low: 'Low',
      out: 'Out',
    },
    form: {
      addTitle: 'New product',
      editTitle: 'Edit product',
      name: 'Name',
      sku: 'SKU',
      barcode: 'Barcode',
      barcodeHint: 'Scan it, let the system create one, or type your own number ("1", "15").',
      barcodeShop: 'Shop code — printed on the label exactly as typed.',
      barcodePlaceholder: 'Scan or generate',
      barcodeGenerate: 'Generate',
      barcodeRegenerate: 'Regenerate',
      barcodeReplaceConfirm:
        'Replace the code? Labels already printed with the old one will stop working.',
      barcodeInternal: 'Store code — we print the label ourselves.',
      barcodeFactory: 'Factory code — the product already carries a label.',
      category: 'Category',
      unit: 'Unit',
      salePrice: 'Sale price',
      minSalePrice: 'Min. sale price',
      costPrice: 'Cost price',
      quantity: 'Stock',
      minThreshold: 'Min. stock',
      hidePrice: 'Hide price from sellers',
      description: 'Description',
      descriptionHint: 'Shown to the seller on the product card',
      noCategory: 'No category',
      delete: 'Delete',
      deleteConfirm: 'Delete this product?',
      belowCost: 'Sale price is below cost',
    },
    stocktakeModal: {
      title: 'Stocktake',
      subtitle: 'Enter the counted quantities — variances will be recorded',
      searchPlaceholder: 'Search product…',
      system: 'System',
      counted: 'Counted',
      variance: 'Variance',
      submit: 'Run stocktake',
      resultTitle: 'Stocktake complete',
      adjusted: 'Adjusted items: {{count}}',
      noChanges: 'No variances found',
      done: 'Done',
    },
  },
  products: {
    title: 'Products & prices',
    subtitle: 'Edit the sale price right in the table',
    catalogCount: '{{count}} products · hidden: {{hidden}}',
    margin: 'MARGIN',
    hidden: 'Hidden',
    hide: 'Hide',
    show: 'Show',
    hiddenNote: 'A hidden product is not shown to cashiers at the register but stays in reports and history. Cost price updates automatically from the latest purchase.',
  },
  cash: {
    title: 'Cash register',
    subtitle: 'all cash movements for the day',
    shiftOpen: 'shift #{{n}} open',
    openingWas: 'at opening: {{value}}',
    balance: 'In the register now',
    incomeToday: 'Income today',
    expenseToday: 'Outflow today',
    opsCount: '{{count}} operations',
    empty: 'No movements today',
    hint: 'Sales and debt payments land here automatically. Expenses, bank collection and change deposits are added manually. The balance is reconciled at shift close.',
    filter: { all: 'All', income: 'Income', expense: 'Outflow' },
    cols: { time: 'TIME', type: 'TYPE', description: 'DESCRIPTION', who: 'WHO', amount: 'AMOUNT' },
    type: {
      opening: 'Opening',
      sale: 'Sale',
      debtPayment: 'Debt payment',
      deposit: 'Deposit',
      expense: 'Expense',
      collection: 'Collection',
      externalPurchase: 'Other shop',
    },
    desc: { receipt: 'Receipt Ч-{{n}}' },
    category: {
      household: 'Household',
      delivery: 'Delivery',
      advance: 'Staff advance',
      other: 'Other',
    },
    newOp: {
      button: 'New operation',
      title: 'New operation',
      category: 'Expense category',
      amount: 'Amount',
      comment: 'Comment',
      commentHint: 'E.g. water, bags',
      available: 'Available in register: {{value}} sum',
      overdraft: 'Amount exceeds the cash balance',
      confirm: 'Record',
    },
  },
  returns: {
    title: 'Returns',
    subtitle: 'this month: {{count}} returns for {{sum}} sum · {{pct}}% of revenue',
    create: 'New return',
    searchPlaceholder: 'Return number, receipt or product…',
    empty: 'No returns yet',
    hint: 'On a return, stock goes back automatically, the amount is deducted from revenue, and the record is logged.',
    cols: { return: 'RETURN', receipt: 'RECEIPT', items: 'ITEMS', reason: 'REASON', money: 'MONEY', sum: 'AMOUNT' },
    reason: { defect: 'Defect', notFit: 'Did not fit', sellerError: 'Seller error', other: 'Other' },
    refund: { cash: 'Cash', card: 'To card', transfer: 'Transfer' },
    findReceipt: 'Find the receipt',
    findPlaceholder: 'Receipt number…',
    changeReceipt: 'Another receipt',
    sold: 'sold: {{qty}}',
    refundMethod: 'Refund the money',
    refundMoney: 'To refund',
  },
  sales: {
    title: 'Sales',
    newSale: 'New sale',
    searchPlaceholder: 'Search by receipt, product, customer…',
    showing: 'Showing {{shown}} of {{total}}',
    empty: 'No sales found',
    walkIn: 'Walk-in',
    allSellers: 'All sellers',
    stats: {
      sum: 'Sales total',
      checks: 'Receipts',
      avg: 'Average check',
      debt: 'On credit',
      today: 'today',
    },
    period: { shift: 'Shift', today: 'Today', yesterday: 'Yesterday', all: 'All', week: 'Week', month: 'Month' },
    payment: {
      all: 'All',
      cash: 'Cash',
      card: 'Card',
      debt: 'On credit',
      terminal: 'Terminal',
      transfer: 'Transfer',
      click: 'Click',
      mixed: 'Mixed',
      credit: 'Credit',
      returned: 'Refunded',
      returns: 'Returns',
    },
    cols: {
      number: 'RECEIPT №',
      time: 'TIME',
      seller: 'SELLER',
      items: 'ITEMS',
      customer: 'CUSTOMER',
      pay: 'PAYMENT',
      sum: 'AMOUNT',
    },
    detail: {
      title: 'Receipt',
      customer: 'Customer',
      status: 'Status',
      qty: 'QTY',
      price: 'PRICE',
      subtotal: 'Subtotal',
      paid: 'Paid',
      remaining: 'Remaining',
      payments: 'Payments',
      noPayments: 'No payments',
      noItems: 'No items',
      invoice: 'Download invoice',
      cancel: 'Cancel sale',
      cancelConfirm: 'Cancel receipt №{{number}}? Items return to stock, cash is reversed from the till.',
      return: 'Return item',
      returnConfirm: 'Confirm return',
    },
    status: {
      draft: 'Draft',
      paid: 'Paid',
      debt: 'On credit',
      partial: 'Partial',
      closed: 'Debt settled',
      cancelled: 'Cancelled',
      returned: 'Returned',
    },
  },
  debts: {
    title: 'Debts',
    subtitle: '{{count}} customers · total debt {{value}} sum',
    subtitleChecks: '{{debts}} debts across {{clients}} customers · total: {{value}} sum · overdue: {{overdue}} sum',
    searchPlaceholder: 'Customer, phone or receipt number…',
    empty: 'No debts',
    noDue: 'No due date',
    overdueTag: 'overdue',
    multi: 'debts: {{count}}',
    multiTitle: 'Customer has several debts',
    paidOf: 'paid {{paid}} of {{total}}',
    checkFor: 'receipt of {{total}}',
    perCheckHint: 'Each debt is a separate receipt with its own due date and balance. Payment is accepted per receipt, so the settlements never get mixed.',
    filters: { all: 'All', overdue: 'Overdue', today: 'Today', upcoming: 'Upcoming' },
    stats: {
      total: 'Total debt',
      overdue: 'Overdue',
      paidToday: 'Paid today',
      paidMonth: 'Repaid this month',
    },
    cols: { customer: 'CUSTOMER', phone: 'PHONE', debts: 'DEBTS', due: 'DUE', sum: 'AMOUNT', check: 'RECEIPT', items: 'ITEMS', remaining: 'REMAINING DEBT' },
    type: { individual: 'Individual', legal: 'Business' },
    pay: 'Accept payment',
    payTitle: 'Accept payment',
    payAmount: 'Payment amount',
    payMethod: 'Payment method',
    payFull: 'Full debt',
    moreDebts: 'This customer has {{count}} debts totaling {{total}} — payment goes to the earliest receipt.',
    remaining: 'Remaining debt',
    methods: { cash: 'Cash', card: 'Card', transfer: 'Transfer', click: 'Click' },
    dueDate: 'Due date',
    setDueDate: 'Set due date',
    check: {
      title: '{{name}} · debt on receipt {{check}}',
      sold: 'Sold',
      hasOthers: 'The customer has more debts: {{count}} — counted separately',
      cols: { product: 'PRODUCT', qty: 'QTY', price: 'PRICE', sum: 'AMOUNT' },
      noItems: 'No items',
      checkTotal: 'Receipt total',
      paid: 'Paid',
      payThis: 'Accept payment for this receipt',
      afterPay: 'Remaining after payment',
      settled: 'receipt will be settled',
    },
  },
  purchases: {
    title: 'Purchases',
    subtitle: 'Deliveries and supplier orders',
    newPurchase: 'New purchase',
    empty: 'No purchases',
    searchPlaceholder: 'Number, supplier or product…',
    deleteConfirm: 'Delete purchase #{{number}}? Its goods receipt will be reversed.',
    stats: {
      count: 'Purchases this month',
      sum: 'Amount this month',
      supplierDebt: 'Owed to suppliers',
      suppliers: 'Suppliers',
    },
    cols: { number: '№', supplier: 'SUPPLIER', items: 'ITEMS', date: 'DATE', sum: 'AMOUNT', status: 'STATUS' },
    status: { unpaid: 'Unpaid', partial: 'Partial', paid: 'Paid' },
    reorder: {
      title: 'Recommended to order',
      positions: '{{count}} items',
      empty: 'All well stocked',
      perDay: '{{qty}} {{unit}}/day',
      daysLeft: '{{qty}} {{unit}} left · {{days}} days',
      outOfStock: 'Out of stock',
      order: 'Order',
    },
    topSuppliers: 'Top suppliers',
    manageSuppliers: 'Manage',
    noSupplier: 'No supplier',
    itemsCount: '{{count}} items',
    newModal: {
      title: 'New goods-receipt',
      subtitle: 'Incoming stock to the warehouse',
      invoice: 'Invoice №',
      addItems: 'Add products',
      quickAdd: 'Running low — quick add',
      empty: 'Find a product and add it to the receipt',
      paidNow: 'Paid now',
      payFull: 'Full amount',
      debtRest: 'Remaining debt',
      payMethod: 'Payment method',
      payMethods: { cash: 'Cash', transfer: 'Transfer' },
      expectedDate: 'Arrival date',
      comment: 'Comment (optional)',
      submit: 'Post receipt',
      inTransit: 'Goods still in transit',
      inTransitHint: 'Stock won’t update until the delivery is accepted',
    },
    delivery: { all: 'All', inTransit: 'In transit', accepted: 'Accepted' },
    detail: {
      title: 'Receipt',
      paid: 'Paid',
      outstanding: 'Outstanding debt',
      payTitle: 'Pay the supplier',
      pay: 'Pay',
      delivery: 'Delivery',
      accept: 'Mark as accepted',
      payFullDebt: 'Full debt',
      inTransitHint: 'Goods are still in transit. Accepting will replenish stock.',
    },
  },
  suppliers: {
    title: 'Suppliers',
    subtitle: 'Supplier directory and their debts',
    subtitleDebt: '{{count}} suppliers · owed to suppliers: {{debt}} sum',
    add: 'Add supplier',
    empty: 'No suppliers',
    searchPlaceholder: 'Search supplier…',
    cols: { name: 'SUPPLIER', phone: 'PHONE', debt: 'DEBT', receipts: 'RECEIPTS' },
    debtLabel: 'Owed to supplier',
    noDebt: 'no debt',
    weOwe: 'we owe',
    payDebt: 'Pay debt',
    fifoHint: 'The payment clears the debt starting from the earliest purchase (FIFO).',
    recentReceipts: 'Recent purchases',
    deleteConfirm: 'Delete supplier “{{name}}”?',
    deleteBlocked: 'Cannot delete: {{count}} receipts, {{debt}} sum owed.',
    form: {
      addTitle: 'New supplier',
      editTitle: 'Edit supplier',
      name: 'Name',
      phone: 'Phone',
      contact: 'Contact person',
      deliveryTerm: 'Delivery term',
      address: 'Address',
      comment: 'Comment',
    },
  },
  customers: {
    title: 'Customers',
    subtitle: 'Customer base · {{count}} records',
    add: 'New customer',
    empty: 'No customers found',
    searchPlaceholder: 'Search by name or phone…',
    cols: { name: 'CUSTOMER', phone: 'PHONE', debt: 'DEBT', type: 'TYPE', purchases: 'PURCHASES', totalBought: 'BOUGHT TOTAL' },
    filters: { all: 'All', withDebt: 'With debt', legal: 'Companies' },
    types: { individual: 'Retail', legal: 'Company' },
    lastBuy: 'last purchase: {{date}}',
    noBuys: 'no purchases yet',
    deleteConfirm: 'Delete customer “{{name}}”?',
    deleteBlocked: 'Cannot delete: {{debts}} debts totalling {{debt}} sum.',
    form: {
      addTitle: 'New customer',
      editTitle: 'Edit customer',
      name: 'Name / company',
      phone: 'Phone',
      type: 'Customer type',
      debtLimit: 'Debt limit',
      noLimit: 'No limit',
      initialDebt: 'Opening debt',
      regular: 'Regular customer',
      regularHint: 'Can buy on credit when the restriction is on',
      comment: 'Comment',
    },
    detail: {
      title: 'Customer',
      totalDebt: 'Current debt',
      debts: 'Debts',
      noDebts: 'No debts',
      recentPurchases: 'Recent purchases',
      noPurchases: 'No purchases yet',
      allSales: 'All customer sales',
      acceptDebt: 'Accept debt payment',
      due: 'due {{date}}',
      status: { open: 'Open', closed: 'Closed' },
    },
  },
  shifts: {
    title: 'Shifts',
    subtitle: 'Cash shifts and cash control',
    subtitleLive: 'open now: {{open}} · this week: {{count}} shifts · till variance: {{diff}} sum',
    openShift: 'Open shift',
    closeShift: 'Close shift',
    open: 'Open',
    closed: 'Closed',
    currentShift: 'Current shift',
    cashier: 'Cashier',
    noOpenShift: 'Shift closed',
    metrics: {
      opening: 'Opening cash',
      cashIn: 'Cash this shift',
      cardIn: 'Card this shift',
      terminal: 'Terminal',
      click: 'Click',
      withdrawals: 'Withdrawals',
      expected: 'Expected in drawer',
    },
    history: 'Shift history',
    cols: { date: 'DATE', cashier: 'CASHIER', period: 'PERIOD', checks: 'RECEIPTS', revenue: 'REVENUE', discrepancy: 'VARIANCE', status: 'STATUS' },
    status: { open: 'Open', balanced: 'Balanced', discrepancy: 'Variance' },
    empty: 'No shifts yet',
    tabs: { journal: 'Shift log', attendance: 'Attendance' },
    filter: { all: 'All', open: 'Open', discrepancy: 'With variance' },
    tender: { cash: 'Cash', card: 'Card', debt: 'On credit', returns: 'Returns' },
    detail: {
      title: 'Shift',
      checks: '{{count}} receipts',
      revenueByTender: 'Revenue by payment method',
      totalRevenue: 'Total revenue',
      cashRecon: 'Cash reconciliation at close',
      externalPayouts: 'Paid to other shop',
      expectedCash: 'Expected in drawer',
      countedCash: 'Counted by seller',
      discrepancy: 'Variance',
      notClosed: 'Shift is still open — reconciliation appears after it closes.',
    },
    attendance: {
      title: 'Attendance',
      scheduleHint: 'schedule: {{from}}–{{to}} · plan to date: {{plan}} h',
      range: { week: 'Week', month: 'Month' },
      cols: { employee: 'EMPLOYEE', shifts: 'SHIFTS', days: 'DAYS', hours: 'HOURS', avg: 'AVG SHIFT', late: 'LATE', plan: 'PLAN COMPLETION' },
      hoursValue: '{{value}} h',
      noLate: 'none',
      lateTimes: '{{count}}×',
      empty: 'No data for the period',
    },
    withdraw: {
      action: 'Cash withdrawal',
      request: 'Withdrawal request',
      amount: 'Amount',
      comment: 'Comment (optional)',
      submitRequest: 'Send request',
      submitWithdraw: 'Withdraw',
      pendingTitle: 'Withdrawal requests',
      noPending: 'No pending requests',
      approve: 'Approve',
      reject: 'Reject',
      from: 'from {{name}}',
      status: { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', notRequired: 'Withdrawal' },
    },
    close: {
      title: 'Close shift',
      expected: 'Expected in drawer',
      counted: 'Counted amount',
      discrepancy: 'Variance',
      confirm: 'Close shift',
    },
    forceClose: {
      action: 'Close',
      title: 'Force-close shift · {{name}}',
    },
    rules: {
      title: 'Shift rules',
      r1: 'Sales are possible only with an open shift',
      r2: 'Cash withdrawal requires owner approval',
      r3: 'On close the cashier enters the counted amount',
      r4: 'Variance is recorded and visible to the owner',
    },
  },
  desktop: {
    title: 'Store app',
    subtitle: 'The register works offline too — installed on the store computer',
    step1: 'Download the file and run it on the store computer.',
    step2: 'On first launch enter the address below, then the store OWNER login and password.',
    step3: 'After that cashiers sign in with their own accounts.',
    urlLabel: 'Address to enter in the app',
    download: 'Download (Windows)',
    version: 'version {{version}}',
    notReady: 'The app is not available for download yet. Please contact support.',
  },

  settings: {
    title: 'Settings',
    subtitle: 'Store, cash register and system parameters',
    saved: 'Changes saved',
    language: {
      title: 'Interface language',
      hint: 'Applied immediately and saved to your account',
      saveError: 'Could not save the language — please try again',
    },
    store: {
      title: 'Store',
      phone: 'Phone',
      address: 'Address',
    },
    cash: {
      title: 'Cash & shifts',
      onlyShift: 'Sales only with an open shift',
      onlyShiftHint: 'The cashier must open a shift before the first sale',
      approval: 'Cash withdrawal requires owner approval',
      approvalHint: 'The admin sends a request, you confirm it',
      debtRegulars: 'Credit sales — regular customers only',
      debtRegularsHint: 'Debt is issued only to a customer in the database',
      debtLimit: 'Debt limit per customer',
      debtLimitHint: 'Credit sales above the limit are blocked',
      discrepancy: 'Allowed cash variance',
    },
    attendance: {
      title: 'Attendance & schedule',
      hint: 'Store working hours — used to compute hours and lateness on the Attendance tab.',
      start: 'Day start',
      end: 'Day end',
      late: 'Late threshold',
    },
    stock: {
      belowCost: 'Block selling below cost price',
      belowCostHint: 'The cashier cannot sell at a loss',
    },
    receipt: {
      title: 'Receipt',
      subtitle: 'Roll size and printing',
      width: 'Roll width',
      widthHint: 'Must match the paper in the printer — otherwise the receipt does not fit and prints one letter per line.',
      mm: 'mm',
      autoPrint: 'Print the receipt automatically',
      autoPrintHint: 'Right after the sale closes, with no print dialog.',
      header: 'Receipt header text',
      footer: 'Receipt footer text',
    },
    label: {
      title: 'Label',
      subtitle: 'Product label roll',
      width: 'Roll width',
      height: 'Label height',
      sizeHint: 'The size of one label on the roll. This is what printing uses.',
      gap: 'Gap',
      gapHint: 'The space between two labels. Almost always 2 mm.',
      offset: 'Vertical shift',
      offsetHint:
        'The layout moves down by this many millimetres. If the test label’s frame sits too high, measure the difference and enter it here.',
      calibrate: 'Calibrate',
      test: 'Test label',
      toolsHint:
        'When you change the roll, press “Calibrate” FIRST — the printer measures the label length itself (one or two come out blank, that is normal). Then check with “Test label”: if the frame sits evenly along the edge, the size is right. Both buttons use the values on screen, so you need not save first.',
      needDesktop: 'Sending to the label printer only works inside the shop app.',
      failed: 'Could not send to the label printer.',
    },
    notify: {
      title: 'Notifications',
      subtitle: 'What to send the owner on Telegram',
      daySummary: 'Day summary on shift close',
      overdue: 'Overdue debts',
      withdrawals: 'Cash withdrawal requests',
      telegramHint: 'Telegram is linked on the Account screen — each employee saves their own Telegram ID.',
    },
    system: {
      title: 'System',
      subtitle: 'Store language, security and audit',
      defaultLanguage: 'Default store language',
      defaultLanguageHint: 'For new employees; everyone keeps their own language',
      inactivityLogout: 'Auto-logout on inactivity',
      inactivityLogoutHint: '0 — never log out automatically',
      minutes: 'min',
      audit: 'Audit log',
      auditHint: 'Record employee actions',
    },
    terminals: {
      title: 'Cloud-paired computer',
      subtitle: 'The register holding the shop database. Only one allowed',
      none: 'No computer is paired yet.',
      pairedAt: 'Paired',
      lastSeen: 'Last contact',
      never: 'never connected',
      revoked: 'Revoked',
      revoke: 'Revoke',
      confirmTitle: 'Disconnect the computer from the cloud',
      confirmBody:
        '«{{name}}» will be disconnected from the cloud. This cannot be undone — a new pairing will be required. It may still hold sales that never reached the cloud: once revoked they will not be sent. Continue only if you are done with the old computer.',
      hint: 'A second register does not belong here — it connects to the server register over the local network.',
    },
  },
  employees: {
    title: 'Employees',
    subtitle: '{{count}} employees · {{active}} active today',
    add: 'Add employee',
    searchPlaceholder: 'Search by name or login…',
    empty: 'No employees found',
    online: 'Online now',
    lastSeen: '{{time}} ago',
    never: 'never signed in',
    block: 'Block',
    unblock: 'Unblock',
    blocked: 'Blocked',
    active: 'Active',
    protected: 'Owner and super-admin are managed separately',
    editTitle: 'Edit employee',
    deleteTitle: 'Delete',
    deleteConfirm: 'Delete employee “{{name}}”?',
    resetPassword: 'Reset password',
    resetPasswordHint: 'Leave empty to keep unchanged',
    periodLabel: 'Results for:',
    period: { today: 'Today', week: 'Week', month: 'Month', year: 'Year' },
    stats: { checks: 'Receipts', revenue: 'Revenue', shifts: 'Shifts' },
    roles: { all: 'All', owner: 'Owner', admin: 'Admin', seller: 'Seller' },
    form: {
      title: 'New employee',
      fullName: 'Full name',
      username: 'Login',
      password: 'Password',
      role: 'Role',
    },
  },
  permissions: {
    title: 'Permissions',
    customized: 'Customized',
    roleDefault: 'Role default',
    profile: 'Access profile',
    preset: { trainee: 'Trainee', seller: 'Seller', senior: 'Senior' },
    discountLimit: 'Discount limit',
    debtLimit: 'Debt per receipt',
    noLimit: 'No limit',
    reset: 'Reset to role',
    byRole: 'by role',
    effectNote: 'Changes take effect on the employee’s next sign-in.',
    savedNote: 'Saved · takes effect on next sign-in.',
    risk: 'RISK',
    capabilities: 'What they can do',
    noCapabilities: 'Basic access only',
    cap: {
      disc: 'discounts up to {{value}}%',
      discFree: 'unlimited discounts',
      debt: 'debt {{value}}',
      debtFree: 'unlimited debt',
      price: 'edits prices',
      ret: 'returns',
      supply: 'goods receipt',
    },
    hint: {
      'sales.edit': 'Change item price — every change is logged',
      'sales.return': 'Process a return without an admin code',
      'sales.delete': 'Delete posted receipts',
      'debts.manage': 'Sell on client credit and accept debt payments',
      'zakup.accept': 'Confirm goods arrival into the warehouse',
      'zakup.delete': 'Delete purchases — their receipts get reversed',
      'products.delete': 'Delete products from the catalogue',
      'suppliers.delete': 'Delete suppliers',
      'customers.delete': 'Delete clients from the base',
      'users.manage': 'Create employees and change their access',
      'cashregister.manage': 'Withdraw cash from the till (collection)',
      'data.costPrice': 'See purchase cost and cost price',
      'data.profit': 'See profit and margin',
      'data.auditLog': 'Access the staff action log',
    },
    groups: {
      dashboard: 'Dashboard',
      sales: 'Sales',
      products: 'Products',
      categories: 'Categories',
      zakup: 'Purchases',
      suppliers: 'Suppliers',
      customers: 'Customers',
      debts: 'Debts',
      cashregister: 'Cash register',
      reports: 'Reports',
      users: 'Employees',
      notifications: 'Notifications',
      data: 'Data access',
    },
    perm: {
      'dashboard.access': 'View dashboard',
      'notifications.access': 'View notifications',
      'products.access': 'View products',
      'products.create': 'Create products',
      'products.edit': 'Edit products',
      'products.delete': 'Delete products',
      'products.export': 'Export products',
      'products.import': 'Import products',
      'categories.access': 'View categories',
      'categories.manage': 'Manage categories',
      'sales.access': 'View sales',
      'sales.create': 'Create sales',
      'sales.edit': 'Edit sales',
      'sales.delete': 'Cancel sales',
      'sales.export': 'Export sales',
      'sales.invoice': 'Print invoice',
      'sales.return': 'Process returns',
      'customers.access': 'View customers',
      'customers.manage': 'Manage customers',
      'customers.delete': 'Delete customers',
      'customers.export': 'Export customers',
      'zakup.access': 'View purchases',
      'zakup.create': 'Create purchases',
      'zakup.accept': 'Accept deliveries',
      'zakup.delete': 'Delete purchases',
      'suppliers.access': 'View suppliers',
      'suppliers.manage': 'Manage suppliers',
      'suppliers.delete': 'Delete suppliers',
      'cashregister.access': 'View cash register',
      'cashregister.manage': 'Manage cash register',
      'reports.access': 'View reports',
      'reports.export': 'Export reports',
      'users.access': 'View employees',
      'users.manage': 'Manage employees',
      'users.shift': 'Manage shifts',
      'debts.access': 'View debts',
      'debts.manage': 'Manage debts',
      'debts.dueDate': 'Change debt due date',
      'data.costPrice': 'View cost price',
      'data.profit': 'View profit',
      'data.cashBalance': 'View cash balance',
      'data.allSalesView': 'View all sales',
      'data.auditLog': 'View audit log',
    },
  },
  audit: {
    title: 'Audit log',
    subtitle: 'Who did what in the system',
    tabs: { log: 'Log', suspicious: 'Suspicious' },
    allActions: 'All actions',
    allUsers: 'All users',
    empty: 'No entries',
    cols: { time: 'TIME', action: 'ACTION', user: 'USER', entity: 'ENTITY' },
    actions: {
      Create: 'Create',
      Update: 'Update',
      Delete: 'Delete',
      Cancel: 'Cancel',
      Return: 'Return',
      Discount: 'Discount',
      MarkDebt: 'On credit',
      Payment: 'Payment',
      Withdraw: 'Withdrawal',
      Deposit: 'Deposit',
      Open: 'Open',
      Close: 'Close',
      Block: 'Block',
      Unblock: 'Unblock',
      Activate: 'Activate',
      Deactivate: 'Deactivate',
      PermissionChange: 'Permission change',
      PriceOverride: 'Price override',
      StockAdjust: 'Stock adjust',
      Stocktake: 'Stocktake',
      Login: 'Login',
      LoginFailed: 'Failed login',
      Logout: 'Logout',
      PasswordChange: 'Password change',
    },
    entities: {
      Sale: 'Sale',
      Product: 'Product',
      Customer: 'Customer',
      Supplier: 'Supplier',
      User: 'Employee',
      Debt: 'Debt',
      Shift: 'Shift',
      CashRegister: 'Cash register',
      Market: 'Store',
      Auth: 'Auth',
    },
    suspicious: {
      clean: 'All clear — no suspicious activity',
      failedLogins: 'Repeated failed logins',
      bulkDelete: 'Bulk deletion',
      errors: 'Recent server errors',
      attempts: '{{count}} attempts',
      deletes: '{{count}} deletions',
    },
  },
  account: {
    title: 'My account',
    subtitle: 'Personal details and sign-in security',
    language: {
      title: 'Interface language',
      subtitle: 'Applied immediately and saved to your account',
      saveError: 'Could not save the language — please try again',
    },
    profile: 'Profile',
    fullName: 'Full name',
    phone: 'Phone',
    telegram: 'Telegram',
    telegramSaved: 'Telegram saved',
    openBot: 'Open the bot',
    telegramLink: 'Telegram bot',
    telegramLinked: 'Linked',
    telegramUnlink: 'Unlink',
    telegramLinkAction: 'Link',
    telegramLinkHint: 'Write any message to the bot — it replies with a 6-digit code. Enter it here (valid for 10 minutes).',
    telegramLinkDone: 'Telegram linked',
    login: 'Login',
    loginLocked: 'login cannot be changed',
    nameLocked: 'name change — via administrator',
    myResults: { title: 'My results · {{month}}', revenue: 'Sales', checks: 'Receipts', avgCheck: 'Average receipt', shifts: 'Shifts worked' },
    password: {
      title: 'Change password',
      hint: 'Minimum 8 characters, letters and digits',
      current: 'Current password',
      new: 'New password',
      repeat: 'Repeat password',
      mismatch: 'Passwords do not match',
      change: 'Change password',
      changed: 'Password changed',
    },
    notify: {
      title: 'Telegram notifications',
      subtitle: 'What to send to your personal Telegram',
      debt: 'Overdue debts',
      stock: 'Out of stock',
      shift: 'Shift close',
      noTelegram: 'Add your Telegram in the profile to receive notifications.',
    },
    sessions: {
      title: 'Devices and sessions',
      subtitle: 'Where your account is signed in',
      thisDevice: 'this device',
      revoke: 'End',
      revokeOthers: 'End all other sessions',
      empty: 'No active sessions',
      now: 'now',
    },
    history: {
      clear: 'Clear',
      title: 'Recent sign-ins',
      subtitle: 'Account login history',
      cols: { time: 'TIME', device: 'DEVICE', status: 'STATUS' },
      success: 'Success',
      failed: 'Wrong password',
      empty: 'No records',
    },
  },
  dashboard: {
    title: 'Owner dashboard',
    newSale: 'New sale',
    shiftOpenAt: 'Shift open · since {{time}}',
    shiftClosed: 'Shift closed',
    stats: {
      salesToday: 'Sales today',
      profitToday: 'Profit today',
      cashInRegister: 'Cash in register',
      customerDebts: 'Customer debts',
      vsYesterday: 'vs yesterday',
      checksCount: '{{count}} receipts',
      margin: 'margin {{value}}',
      withdrawalsToday: 'withdrawals today: {{count}} · for {{amount}}',
      noWithdrawals: 'no withdrawals today',
      overdueCount: '{{count}} overdue',
      totalCustomers: '{{count}} customers total',
      sellersOnShift: 'Sellers on shift',
      onShiftNow: 'at the till now: {{count}}',
      noOpenShift: 'no open shifts',
      stockSignals: 'Stock signals',
      outCount: '{{count}} out of stock',
      lowCount: '{{count}} running low',
      stockOk: 'stock is fine',
    },
    chart: {
      title: 'Weekly revenue',
      total: 'Total',
    },
    recent: {
      title: 'Recent sales',
      all: 'All sales',
      empty: 'No sales yet',
      openHint: 'Open receipt — edit, void, refund',
      cols: { time: 'TIME', seller: 'SELLER', items: 'ITEMS', pay: 'PAYMENT', sum: 'AMOUNT' },
    },
    purchases: {
      title: 'Purchases',
      all: 'All purchases',
      empty: 'No purchases yet',
      inTransit: 'In transit',
      accepted: 'Accepted',
      positions: '{{count}} items',
      accept: 'Accept',
    },
    sellerAccess: {
      title: 'Seller access',
      configure: 'Configure',
      empty: 'No sellers added',
      active: 'Active',
      blocked: 'Blocked',
    },
    attention: {
      title: 'Needs attention',
      open: { shift: 'open shift reconciliation', warehouse: 'open warehouse', debt: 'open debts', supply: 'open delivery', default: 'open' },
    },
    lowStock: {
      title: 'Running low in stock',
      order: 'Create purchase',
      empty: 'All well stocked',
    },
    payments: {
      title: 'Upcoming payments',
      all: 'All debts',
      empty: 'No upcoming payments',
      due: 'due {{date}}',
      today: 'today',
      inDays: 'in {{count}} days',
      overdueDays: 'overdue {{count}} days',
    },
    shift: {
      title: 'Shift · today',
      cashier: 'Cashier',
      openedAt: 'Opened',
      checks: 'Receipts',
      avgCheck: 'Average check',
      noShift: 'No open shift',
    },
  },
  reports: {
    title: 'Reports',
    subtitle: 'Financial picture of the business',
    download: 'Download report',
    period: { week: 'Week', month: 'Month', quarter: 'Quarter' },
    stats: {
      revenue: 'Revenue',
      profit: 'Profit',
      expenses: 'Purchase costs',
      avgCheck: 'Average check',
      vsPrevPeriod: 'vs previous period',
      margin: 'margin {{value}}',
      purchCount: '{{count}} purchases',
      checksCount: '{{count}} receipts',
      checksReturns: '{{count}} receipts · returns: {{returns}}',
    },
    chart: {
      title: 'Revenue and profit by day',
      revenue: 'Revenue',
      profit: 'Profit',
      best: 'best',
    },
    categories: {
      title: 'Sales by category',
      empty: 'No data for the period',
    },
    payments: {
      title: 'Payment methods',
      empty: 'No data for the period',
      debtRisk: 'On credit {{value}} — a high share. Watch debt repayment.',
    },
    topProducts: {
      title: 'Top-5 products',
      empty: 'No sales for the period',
      cols: { product: 'PRODUCT', sold: 'SOLD', revenue: 'REVENUE' },
    },
    staff: {
      title: 'Sellers',
      empty: 'No data for the period',
      cols: { seller: 'SELLER', checks: 'RECEIPTS', revenue: 'REVENUE', share: 'SHARE' },
      checksAvg: '{{count}} receipts · avg {{avg}}',
      shareNote: 'Share of the period revenue',
    },
  },
  labels: {
    popupBlocked: 'The browser blocked the print window — the file was downloaded instead. Check the printer under Devices.',
    printFailed: 'Could not open the print dialog. Check the browser settings.',
    title: 'Print labels',
    subtitle: '{{count}} products',
    print: 'Print {{count}} labels',
    pcs: 'pcs',
    size: 'Size',
    mm: 'mm',
    sizeFromSettings: '— roll from Settings',
    previewCaption: 'Preview — this is what prints ({{w}}×{{h}} mm)',
    previewFailed: 'Preview unavailable',
    willGenerate: 'code will be created',
    generateNote: '{{count}} products have no barcode — one is created automatically before printing.',
    printerHint:
      'In the print dialog pick the label printer and set scale to 100%. A printer set to A4 prints the label tiny in the corner of the sheet.',
    fromProduct: 'Print label',
    selected: '{{count}} selected',
    selectAll: 'Select all on page',
    clearSelection: 'Clear',
    afterAccept: 'Goods received. Print labels?',
    later: 'Later',
  },
  pos: {
    title: 'New sale',
    creditApplied: 'Credited from the customer advance',
    back: 'To sales',
    park: 'Park',
    held: 'Parked receipts',
    leaveTitle: 'Sale not finished',
    leaveMessage:
      'Park this receipt? It stays in the “Parked” list and you can pick it up again any time.',
    stay: 'Stay at the register',
    discard: 'Delete receipt',
    discardConfirm: 'Delete this unfinished receipt? The goods go back to stock.',
    searchPlaceholder: 'Scan a barcode or search by name…',
    scan: {
      notFound: '{{code}} — no product with this barcode',
    },
    emptyCart: 'Cart is empty — find a product to start a sale',
    cols: {
      product: 'PRODUCT',
      qty: 'QTY',
      price: 'PRICE',
      subtotal: 'SUBTOTAL',
    },
    quantity: 'Quantity',
    price: 'Price',
    subtotal: 'Subtotal',
    discount: 'Discount',
    remove: 'Remove',
    customer: {
      label: 'Customer',
      walkIn: 'Walk-in',
      add: 'Add customer',
      searchPlaceholder: 'Search customer by name or phone…',
    },
    total: 'Total',
    amount: 'Amount due',
    mix: {
      rest: 'rest',
      remainder: 'Remainder',
    },
    done: {
      title: 'Sale completed',
      print: 'Print receipt',
      finish: 'Finish',
      receipt: 'Receipt',
      shift: 'Shift',
      thanks: 'Thank you for your purchase!',
      printProblem: 'Receipt not printed:',
      printFailed: 'could not reach the printer.',
    },
    payment: {
      title: 'Payment method',
      cash: 'Cash',
      card: 'Card',
      transfer: 'Transfer',
      click: 'Click',
      debt: 'On credit',
      mixed: 'Mixed',
    },
    dueDate: 'Due date',
    checkout: 'Complete sale',
    success: 'Sale completed',
  },
  landing: {
    nav: {
      login: 'Sign in',
    },
    hero: {
      eyebrow: 'ERP for the construction business',
      title: 'Bring order to your construction store',
      subtitle:
        'Sales, inventory, customer debts and reporting — in one system. 6 modules, 3 roles: owner, admin and cashier.',
      bullets: {
        b1: 'A sale in 15 seconds',
        b2: 'Stock updated after every receipt',
        b3: 'Daily report to Telegram at 21:00',
      },
    },
    modules: {
      eyebrow: 'What’s inside',
      title: 'Six modules — the whole store',
      sales: {
        title: 'Sales and checkout',
        desc: 'A sale is rung up in seconds — no queue builds at the counter. Shifts and cash stay under control.',
      },
      warehouse: {
        title: 'Inventory tracking',
        desc: 'Stock levels in real time — from cement to rebar, in any unit of measure.',
      },
      debts: {
        title: 'Customer debts',
        desc: 'Every debt is recorded: who owes, how much and when they promised to repay.',
      },
      purchases: {
        title: 'Purchasing and suppliers',
        desc: 'Incoming goods, suppliers and purchase prices — all in one ledger.',
      },
      employees: {
        title: 'Staff and permissions',
        desc: 'Owner, admin and salespeople — each with their own permissions and work shifts.',
      },
      reports: {
        title: 'Reports and analytics',
        desc: 'Revenue, profit and stock movement — a ready report in one click, no manual math.',
      },
    },
    cta: {
      eyebrow: 'Request',
      title: 'Request access',
      subtitle:
        'A sale in seconds, a report in one click. Leave your name and phone — our admin will get in touch.',
      nameLabel: 'Name',
      namePlaceholder: 'Enter your name',
      phoneLabel: 'Phone',
      phonePlaceholder: '+998 __ ___ __ __',
      submit: 'Send request',
      success: 'Request sent — we’ll be in touch soon',
      privacy: 'Your data is used only to contact you',
    },
    footer: {
      privacy: 'Privacy policy',
      tagline: 'ERP for the construction business',
    },
  },
  placeholder: {
    soon: 'Section under construction',
  },
  // SuperAdmin console — mockups are Russian-only; keys exist in all three
  // languages so later screens don't need a retro-fit (TZ §A4).
  sa: {
    nav: {
      dashboard: 'Overview',
      requests: 'Requests',
      stores: 'Stores',
      billing: 'Subscriptions',
      users: 'Users',
      settings: 'Settings',
    },
    auth: {
      title: 'Platform console',
      subtitle: 'Buildix administrators only.',
      notSuperAdmin: 'This page is for the platform administrator only.',
    },
    placeholder: {
      title: 'Section in progress',
      body: 'The console shell works: navigation, theme and access control. This screen is filled in during phase {{phase}}.',
    },
    dashboard: {
      title: 'Buildix overview',
      subtitle: 'every store on the platform',
      kpi: {
        activeStores: 'Active stores',
        newThisMonth: '+{{count}} this month',
        newRequests: 'New requests',
        fromWelcome: 'from the welcome page',
        revenue: 'Subscription revenue',
        revenueSoon: 'arrives with the plans',
        perMonth: 'UZS/mo',
        overdue: 'Payment overdue',
        overdueHint: 'send a payment reminder',
      },
      systemsOk: 'All systems operational',
      systemsDown: 'No connection to the server',
      acceptedHint: 'accepted — contact the client',
      rejectedHint: 'rejected',
      requestsTitle: 'Sign-up requests',
      viewAll: 'all requests',
      noRequests: 'No new requests',
      attentionTitle: 'Needs attention',
      allGood: 'All clear',
      overdueLine: '“{{name}}” — payment {{days}} days overdue',
      overdueHelp: 'call the owner or restrict access',
      expiringLine: 'Subscriptions expiring this week: {{count}}',
      storesTitle: 'Stores',
      byLastActivity: 'last activity',
    },
    store: {
      status: { active: 'Active', overdue: 'Overdue', blocked: 'Blocked' },
      paidUntil: 'until {{date}}',
      noExpiry: 'no expiry',
      users: '{{count}} users',
      block: 'Block',
      unblock: 'Unblock',
      blockedByOperator: 'Blocked by the platform operator',
      since: 'since {{date}}',
      ownerTitle: 'Owner',
      owner: 'Name',
      phone: 'Phone',
      subscriptionTitle: 'Subscription',
      plan: 'Plan',
      planSoon: 'arrives in S3',
      paidUntilLabel: 'Paid until',
      statsTitle: 'Activity',
      usersLabel: 'Users',
      checksThisMonth: 'Receipts this month',
      lastActivity: 'Last activity',
      outstandingDebt: 'Customer debt',
      paymentsTitle: 'Payment history',
      paymentsSoon: 'Arrives together with subscriptions and payments.',
      blockedReason: 'Block reason',
      blockWarning: 'Blocking instantly closes sign-in for EVERY user of the store. The data is kept.',
      blockFull: 'Block store',
      unblockFull: 'Unblock store',
    },
    requests: {
      title: 'Sign-up requests',
      subtitle: 'submitted on the welcome page (name + phone)',
      tabs: { new: 'New', accepted: 'Accepted', rejected: 'Rejected', all: 'All' },
      col: { applicant: 'Applicant', phone: 'Phone', received: 'Received', status: 'Status' },
      status: { new: 'New', accepted: 'Accepted', connected: 'Connected', rejected: 'Rejected' },
      searchPlaceholder: 'Name or phone…',
      empty: 'No requests in this category',
      call: 'Call',
      accept: 'Accept',
      reject: 'Reject',
      reopen: 'Undo',
      createStore: 'Create store',
      storeCreated: 'store created',
      rejectedByOperator: 'Rejected by operator',
      workflowHint:
        'Workflow: call the applicant → "Accept" → "Create store" — the store and the owner account are created. The address and username can go by SMS; hand over the password in person.',
    },
    create: {
      title: 'Create store',
      storeName: 'Store name',
      ownerName: 'Owner name',
      ownerPhone: 'Phone',
      standaloneHint: 'New store without a request',
      newStore: 'Create store',
      address: 'Store address',
      addressHint: 'derived from the name — the owner and their staff sign in at this address',
      username: 'Owner username',
      password: 'Password',
      generate: 'Generate',
      period: 'Paid for',
      months: '{{count}} mo.',
      paidUntil: 'Paid until:',
      free: 'available',
      taken: 'taken',
      submit: 'Create store',
      passwordWarning:
        'The password is shown ONCE and stored nowhere. Copy it now and hand it over in person — passwords are never sent by SMS.',
      doneTitle: 'Store created',
      doneBody:
        'Give the owner the address and username. Copy the password now — once this window closes it cannot be recovered, only reset.',
    },
    stores: {
      title: 'Stores',
      subtitle: 'every store, its plan and status',
      summary: '{{total}} stores · active: {{active}} · overdue: {{overdue}} · blocked: {{blocked}}',
      filters: { all: 'All', active: 'Active', overdue: 'Overdue', blocked: 'Blocked' },
      col: {
        store: 'Store',
        owner: 'Owner',
        plan: 'Plan',
        paidUntil: 'Paid until',
        users: 'Users',
        status: 'Status',
      },
      searchPlaceholder: 'Name, owner or city…',
      empty: 'Nothing found',
    },
    billing: {
      title: 'Subscriptions',
      subtitle: 'plans, payments and overdue accounts',
      summary: 'expected monthly: {{expected}} UZS · overdue: {{overdue}} UZS',
      tabs: { all: 'All', soon: 'Due soon', overdue: 'Overdue' },
      status: { paid: 'Paid', soon: 'Due soon', overdue: 'Overdue' },
      plans: { Start: 'Start', Standard: 'Standard', Pro: 'Pro' },
      planStores: '{{count}} stores',
      planLimits: 'up to {{points}} points · users: {{users}}',
      unlimited: 'unlimited',
      channels: { Cash: 'Cash', Click: 'Click', Payme: 'Payme', Transfer: 'Bank transfer' },
      col: { amount: 'Amount/mo' },
      lastPayment: 'last payment: {{date}} · {{channel}}',
      noPayments: 'no payments yet',
      searchPlaceholder: 'Store…',
      payAction: 'Payment received',
      payTitle: 'Payment received',
      payConfirm: 'Record payment',
      period: 'Period',
      channel: 'Payment method',
      amount: 'To record',
      note: 'Note',
      notePlaceholder: 'optional — receipt number etc.',
      anchorOnExpiry: 'Extended from the previous date — the billing day does not move.',
      anchorOnToday: 'The grace period had lapsed and access was closed — counted from today.',
      anchorManual: 'The date was set manually.',
      customMonths: 'or',
      customDate: 'Date',
      autoDate: 'automatic',
      remindAll: 'Remind all overdue',
      remindResult: 'delivered: {{sent}} · no Telegram: {{unreachable}}',
      noTelegram: 'no Telegram',
      noTelegramHint: 'The owner has not linked Telegram — the reminder will not arrive, call them instead.',
      recentTitle: 'Recent payments',
      noPaymentsYet: 'No payments yet',
    },
    users: {
      title: 'Platform users',
      subtitle: 'every staff member across all stores',
      summary: '{{total}} users · owners: {{owners}} · admins: {{admins}} · sellers: {{sellers}} (on this page)',
      roles: { Owner: 'Owner', Admin: 'Admin', Seller: 'Seller' },
      col: { user: 'User', role: 'Role', store: 'Store', lastLogin: 'Last sign-in' },
      searchPlaceholder: 'Name, username or phone…',
      allStores: 'All stores',
      resetPassword: 'Reset password',
      resetTitle: 'New password',
      resetConfirm: 'Set password',
      resetWarning: 'The old password stops working immediately and every session of this user is closed. Hand the new password over in person — it is never sent by SMS.',
      resetDone: 'Password changed',
      resetDoneBody: 'Copy the password now — once this window closes it cannot be recovered, only reset again.',
      scopeHint: 'The superadmin controls platform access only: password and blocking. Roles and in-store permissions are set by the store owner or admin.',
    },
    settings: {
      title: 'Platform settings',
      subtitle: 'plans, payment, SMS and blocking rules — for every store',
      saved: 'Settings saved',
      plansTitle: 'Plans',
      plansHint: 'monthly price in UZS — applies from the next payment',
      notifyTitle: 'Store notifications (Telegram)',
      notifyHint: 'sent to the store owner over Telegram. SMS is not used — messages are free, delivery is confirmed, and the owner can pull reports from the same chat.',
      notifyExpiring: 'Subscription ending soon',
      notifyExpiringSub: '{{count}} days before it expires',
      expiryReminderDays: 'Remind this many days ahead',
      notifyBlocked: 'Store blocked',
      notifyBlockedSub: 'immediately on blocking — with the reason',
      blockTitle: 'Blocking rules',
      blockHint: 'what happens when payment is overdue',
      graceDays: 'Grace days after the subscription ends',
      warnOnOverdue: 'Warning banner in the app',
      warnOnOverdueSub: 'shown to owner and admin from the first overdue day',
      restrictAfterGrace: 'Read-only mode',
      restrictAfterGraceSub: 'after the grace period: sales and goods receipt are blocked, data stays visible',
      fullBlockAfterDays: 'Full sign-in block (days)',
      fullBlockAfterDaysSub: '0 — never block sign-in',
      supportTitle: 'Support contacts',
      supportHint: 'shown on the sign-in page',
      supportPhone: 'Phone',
      supportTelegram: 'Telegram',
      supportEmail: 'E-mail',
      applyHint: 'Blocking rules take effect the moment you save.',
    },
  },
};
